/**
 * IntercomProvider, boots Intercom with authenticated user data.
 *
 * Wraps the app inside AuthProvider. Automatically:
 * - Boots Intercom when user is logged in (with profile + subscription data)
 * - Shuts down Intercom on logout
 * - Updates Intercom attributes when profile/subscription changes
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { bootIntercom, bootVisitorIntercom, launcherPosition, shutdownIntercom, updateIntercom } from '../lib/intercom';
import { API_BASE } from '../lib/apiConfig';

export default function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const bootedRef = useRef(false);

  /* v3 Phase 4r2: on the new /case/:id workspace the chat is in-platform,
     so Intercom shouldn't boot or float over the page. When the user
     navigates ONTO /case/*, shut Intercom down; when they leave, the
     boot effect below re-boots on the next user-state change. Until we
     fully decommission Intercom this is the cleanest split, old screens
     keep the widget, new case-file screens use the native composer. */
  /* Except /case/draft (2026-10-16): a free account hasn't got a
     specialist chat yet, and that's the page where people decide whether
     to pay, so they need a way to ask a question. Patty sat on it for 10
     minutes on her phone with nowhere to ask. */
  /* 16 Oct 2026: the bubble now shows on every page, case pages included,
     so people can always reach us. Left as a flag so it's easy to turn off
     on case pages again if it clashes with the case chat. */
  const onCaseRoute = false;

  useEffect(() => {
    if (onCaseRoute && bootedRef.current) {
      shutdownIntercom();
      bootedRef.current = false;
    }
  }, [onCaseRoute]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // User logged out, shut down Intercom
      if (bootedRef.current) {
        shutdownIntercom();
        bootedRef.current = false;
      }
      // Not signed in (login, signup, code screen): still show the bubble,
      // as an anonymous visitor.
      bootVisitorIntercom();
      return;
    }

    // On the /case/:id route, native chat replaces Intercom, skip boot.
    if (onCaseRoute) return;

    // Fetch profile + subscription and boot Intercom
    (async () => {
      try {
        const [profileRes, subRes, billsRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('first_name, last_name, phone_e164, onboarding_completed, created_at')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('subscriptions')
            .select('plan, status, bills_used, bills_limit')
            .eq('user_id', user.id)
            .maybeSingle(),
          supabase
            .from('bills')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
        ]);

        const profile = profileRes.data;
        const sub = subRes.data;
        const billCount = billsRes.count || 0;

        const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || undefined;
        const createdAt = profile?.created_at
          ? Math.floor(new Date(profile.created_at).getTime() / 1000)
          : undefined;

        // Fetch HMAC hash from server if identity verification is set up
        let userHash: string | undefined;
        try {
          /* This used to call /api/intercom-hash on the app's own domain,
             which only serves the app's HTML, so no hash ever came back.
             The endpoint lives on the API server and needs the session. */
          const { data: sess } = await supabase.auth.getSession();
          const hashRes = await fetch(`${API_BASE}/api/intercom-hash`, {
            headers: sess.session?.access_token ? { Authorization: `Bearer ${sess.session.access_token}` } : {},
          });
          if (hashRes.ok) {
            const hashData = await hashRes.json();
            userHash = hashData.hash;
          }
        } catch {
          // Identity verification not configured, continue without it
        }

        bootIntercom({
          userId: user.id,
          email: user.email || '',
          name,
          createdAt,
          userHash,
          phone: profile?.phone_e164 || undefined,
          plan: sub?.plan || 'free',
          billsUsed: sub?.bills_used ?? billCount,
          billsLimit: sub?.bills_limit || undefined,
          subscriptionStatus: sub?.status || undefined,
          onboardingCompleted: profile?.onboarding_completed ?? false,
        });

        bootedRef.current = true;
      } catch (err) {
        console.error('Failed to boot Intercom:', err);
      }
    })();
  }, [user, loading, onCaseRoute]);

  // Keep the bubble clear of the phone nav bar and the draft page's start
  // button as people move between pages.
  useEffect(() => {
    updateIntercom(launcherPosition(location.pathname));
  }, [location.pathname]);

  // Periodically update Intercom attributes (every 5 min) so custom data stays fresh
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      try {
        const { data: sub } = await supabase
          .from('subscriptions')
          .select('bills_used, status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (sub) {
          updateIntercom({
            bills_used: sub.bills_used,
            subscription_status: sub.status,
          });
        }
      } catch {
        // Silent fail
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  return <>{children}</>;
}
